import React, { useMemo, useState, useCallback } from 'react';
import { Layout, Menu, theme, Button, Typography, Modal } from 'antd';
import {
  HomeOutlined,
  DatabaseOutlined,
  ToolOutlined,
  TeamOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext, normalizeCustomerName } from '../auth/customerContext';
import DashboardAccountMenu from '../components/dashboard/DashboardAccountMenu';
import { LegalLinks } from '../legal/LegalLayout';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

export default function AppLayout({
  children,
  title = 'ManageLift',
  frameless = false,
  contentClassName,
  contentStyle,
  contentInnerStyle,
  headerClassName,
  headerStyle,
  renderHeaderActions,
  productDashboardShell,
}) {
  const {
    token: { colorBgContainer, boxShadowSecondary },
  } = theme.useToken();

  const { session, logout } = useAuth();
  const activeCustomerName = resolveActiveCustomerContext(session).customerName;
  const displayTitle = normalizeCustomerName(title) || activeCustomerName || 'ManageLift';

  const location = useLocation();
  const navigate = useNavigate();
  const isPromotedCustomerDashboard =
    location.pathname === '/fleet_dashboard' ||
    productDashboardShell === 'customer';
  const isOperatorDashboardPattern =
    location.pathname === '/operator' || productDashboardShell === 'operator';
  const usesProductDashboardShell =
    isPromotedCustomerDashboard ||
    isOperatorDashboardPattern ||
    Boolean(productDashboardShell);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [pendingLogout, setPendingLogout] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const role = useMemo(() => {
    const r =
      session?.role ||
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('userRole')) ||
      '';
    return String(r).toLowerCase();
  }, [session?.role]);

  const base = useMemo(() => {
    let seg = location.pathname.split('/').filter(Boolean)[0] || 'customer';

    if (seg === 'technician-dashboard') seg = 'technician';
    if (seg === 'dashboard') seg = 'technician';
    if (seg === 'maintenance') seg = 'technician';
    if (seg === 'repairs') seg = 'technician';

    const allowed = new Set(['customer', 'technician', 'operator']);
    if (!allowed.has(seg)) {
      if (role === 'operator') seg = 'operator';
      else if (role === 'technician') seg = 'technician';
      else seg = 'customer';
    }

    return `/${seg}`;
  }, [location.pathname, role]);

  const selectedKey = useMemo(() => {
    if (role === 'operator') return 'dash';
    if (location.pathname.startsWith(`${base}/maintenance`)) return 'maint';
    if (location.pathname.startsWith(`${base}/forklifts`)) return 'forks';
    if (location.pathname.startsWith(`${base}/users`)) return 'users';
    if (location.pathname.startsWith(`${base}/reports`)) return 'reports';
    return 'dash';
  }, [location.pathname, base, role]);

  const tryNavigate = useCallback(
    (path) => {
      const dirty = localStorage.getItem('dirtyForm') === '1';
      if (dirty) {
        setPendingPath(path);
        setPendingLogout(false);
        setShowConfirm(true);
        return;
      }
      navigate(path);
      if (mobileNav) setCollapsed(true);
    },
    [mobileNav, navigate]
  );

  const tryLogout = () => {
    const dirty = localStorage.getItem('dirtyForm') === '1';
    if (dirty) {
      setPendingPath(null);
      setPendingLogout(true);
      setShowConfirm(true);
      return;
    }
    logout();
  };

  const items = useMemo(() => {
    if (!role) {
      return [
        {
          key: 'dash',
          icon: <HomeOutlined />,
          label: 'Dashboard',
          onClick: () => tryNavigate('/login'),
        },
      ];
    }

    if (role === 'operator') {
      return [
        {
          key: 'dash',
          icon: <HomeOutlined />,
          label: 'Dashboard',
          onClick: () => tryNavigate('/operator'),
        },
      ];
    }

    const list = [
      {
        key: 'dash',
        icon: <HomeOutlined />,
        label: 'Dashboard',
        onClick: () => tryNavigate(role === 'customer' ? '/fleet_dashboard' : base),
      },
      {
        key: 'forks',
        icon: <DatabaseOutlined />,
        label: 'Forklifts',
        onClick: () => tryNavigate(`${base}/forklifts`),
      },
    ];


    if (role === 'customer' || role === 'technician') {
      list.push({
        key: 'maint',
        icon: <ToolOutlined />,
        label: role === 'technician' ? 'Records' : 'Maintenance',
        onClick: () => tryNavigate(`${base}/maintenance`),
      });
    }

    if (role === 'customer') {
      list.push({ key: 'reports', icon: <FileTextOutlined />, label: 'Reports', onClick: () => tryNavigate('/customer/reports') });
    }

    if (role === 'customer' && session?.canManageCustomerUsers === true) {
      list.push({
        key: 'users',
        icon: <TeamOutlined />,
        label: 'Users',
        onClick: () => tryNavigate(`${base}/users`),
      });
    }

    return list;
  }, [base, role, session?.canManageCustomerUsers, tryNavigate]);

  const headerActions = renderHeaderActions
    ? renderHeaderActions({
        displayTitle,
        activeCustomerName,
        session,
        logout: tryLogout,
        navigate: tryNavigate,
      })
    : null;

  const handleConfirmLeave = () => {
    localStorage.removeItem('dirtyForm');
    setShowConfirm(false);
    if (pendingLogout) {
      setPendingLogout(false);
      logout();
      return;
    }
    if (pendingPath) {
      const path = pendingPath;
      setPendingPath(null);
      navigate(path);
      if (mobileNav) setCollapsed(true);
    }
  };

  const handleStay = () => {
    setShowConfirm(false);
    setPendingPath(null);
    setPendingLogout(false);
  };

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          className={
            isPromotedCustomerDashboard
              ? 'customer-preview-sider'
              : isOperatorDashboardPattern
                ? 'operator-dashboard-sider'
                : undefined
          }
          width={220}
          breakpoint="lg"
          collapsedWidth="0"
          collapsed={collapsed}
          onBreakpoint={(broken) => {
            setMobileNav(broken);
            setCollapsed(broken);
          }}
          onCollapse={(nextCollapsed) => setCollapsed(nextCollapsed)}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: mobileNav ? 1000 : undefined,
          }}
        >
          <div
            style={{
              height: usesProductDashboardShell ? 58 : 64,
              minHeight: usesProductDashboardShell ? 58 : 64,
              display: 'flex',
              alignItems: 'center',
              gap: usesProductDashboardShell ? 14 : 20,
              margin: usesProductDashboardShell ? '0 12px 8px' : '0 16px',
              paddingInline: usesProductDashboardShell ? 12 : 8,
              ...(usesProductDashboardShell
                ? {
                    borderBottom: '1px solid rgba(255,255,255,0.10)',
                    background: 'rgba(255,255,255,0.018)',
                  }
                : {}),
            }}
          >
            <img
              src="/Logo.png"
              alt="ManageLift"
              style={{ height: 28, display: 'block' }}
            />
            <span
              style={{
                color: 'rgba(255,255,255,0.95)',
                fontWeight: 700,
                letterSpacing: 0.3,
                fontSize: 18,
              }}
            >
              ManageLift
            </span>
          </div>

          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={items}
          />
        </Sider>

        {mobileNav && !collapsed ? (
          <div
            aria-hidden="true"
            onClick={() => setCollapsed(true)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.32)',
              zIndex: 999,
            }}
          />
        ) : null}

        <Layout style={{ marginLeft: mobileNav || collapsed ? 0 : 220 }}>
          <Header
            className={headerClassName}
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              padding: '0 16px',
              background: colorBgContainer,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: boxShadowSecondary,
              ...(headerStyle || {}),
            }}
          >
            <Title level={4} style={{ margin: 0 }}>
              {displayTitle}
            </Title>
            {headerActions || (
              <DashboardAccountMenu
                customerName={activeCustomerName}
                logout={tryLogout}
                navigate={tryNavigate}
                session={session}
              />
            )}
          </Header>

      <Content
            className={contentClassName}
            style={{
              margin: frameless ? 0 : '24px 16px 0',
              ...(contentStyle || {}),
            }}
          >
            <div
              style={{
                padding: frameless ? 0 : 24,
                background: frameless ? 'transparent' : colorBgContainer,
                minHeight: 360,
                margin: '0 auto',
                width: '100%',
                maxWidth: frameless ? 'none' : 1200,
                borderRadius: frameless ? 0 : 8,
                ...(contentInnerStyle || {}),
              }}
            >
              {children}
            </div>
          </Content>
          <footer style={{padding:'18px',textAlign:'center',background:colorBgContainer}}><LegalLinks /></footer>
        </Layout>
      </Layout>

      <Modal
        open={showConfirm}
        title="Unsaved Changes"
        onCancel={handleStay}
        footer={[
          <Button
            key="stay"
            type="primary"
            style={{ borderRadius: 999 }}
            onClick={handleStay}
          >
            Stay on Page
          </Button>,
          <Button
            key="leave"
            type="primary"
            danger
            style={{ borderRadius: 999 }}
            onClick={handleConfirmLeave}
          >
            Leave Page
          </Button>,
        ]}
        centered
        maskClosable={false}
      >
        <p>
          You have unsaved changes on this form. If you leave this page, your entries
          will not be saved.
        </p>
      </Modal>
    </>
  );
}
